import {useField} from 'formik'
import {FC, ReactNode, useState} from 'react'

import {IncludedPriceBenefits} from '@/components/common/benefits/IncludedPriceBenefits'
import {TextVariant} from '@/components/common/content/Text'
import {usePreferences} from '@/components/context/Preferences'
import {AllMarketsButton} from '@/components/form/markets/AllMarketsButton'
import {useMarketsModalContext} from '@/components/form/markets/context/MarketsModalContext'
import {MarketsContactBanner} from '@/components/form/markets/MarketsContactBanner'
import {AllMarketsModal} from '@/components/form/markets/selection/modal/AllMarketsModal'
import {SelectedMarketsList} from '@/components/form/markets/selection/SelectedMarketsList'
import {getRegionalCountryList} from '@/content/countryCards'
import {useTrademarkVerificationContent} from '@/content/trademarkVerification'
import {useTranslation} from '@/i18n'
import {Jurisdiction} from '@/utils/countries/constants'
import {analytics} from '@/utils/tracking/analytics'

import {MarketsCheckboxList} from '../MarketsCheckboxList'

type MarketsSelectionWithModalProps = {
  selectedMarkets: Jurisdiction[]
  numOfVisibleMarkets: number
  priceBenefits?: ReactNode
  summary: ReactNode
  alreadyCoveredMarkets?: Jurisdiction[]
}

export const MarketsSelectionWithModal: FC<MarketsSelectionWithModalProps> = ({
  selectedMarkets,
  numOfVisibleMarkets,
  priceBenefits,
  summary,
  alreadyCoveredMarkets,
}) => {
  const {t} = useTranslation()
  const {
    selection: {title, description, selectButtonText, addMoreButtonText},
    allMarketsButton,
    feesDescription,
    modalTitle,
  } = useTrademarkVerificationContent().markets

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [field, , {setValue}] = useField<Jurisdiction[]>('jurisdictions')
  const {isMarketsModalVisible, setIsMarketsModalVisible} = useMarketsModalContext()
  const {origin, destination} = usePreferences()

  const regionalList = getRegionalCountryList(origin, destination)
  const visibleMarkets = regionalList.slice(0, numOfVisibleMarkets)

  const isCheckboxListVisible =
    !isMarketsModalVisible &&
    selectedMarkets.every((i) => visibleMarkets.includes(i as Jurisdiction))

  const openModal = () => {
    setIsModalVisible(true)
    analytics.track('Modal - All countries - is open')
  }

  const closeModal = () => {
    if (selectedMarkets.length) {
      setIsMarketsModalVisible(true)
    }
    setIsModalVisible(false)
    analytics.track('Modal - All countries - is closed')
  }

  const verificationPriceBenefits = (
    <IncludedPriceBenefits description={feesDescription} textVariant={TextVariant.h5} />
  )

  const onRemove = (jurisdiction: Jurisdiction) =>
    setValue(field.value.filter((country) => country !== jurisdiction))

  return (
    <>
      {isCheckboxListVisible ? (
        <>
          <MarketsCheckboxList
            alreadyCoveredMarkets={alreadyCoveredMarkets}
            numOfMarkets={numOfVisibleMarkets}
          />
          <AllMarketsButton
            title={t(allMarketsButton.title)}
            description={t(allMarketsButton.description)}
            onClick={openModal}
          />
        </>
      ) : (
        <SelectedMarketsList
          selectedJurisdictions={selectedMarkets}
          title={t(title)}
          description={t(description)}
          adjustButtonText={t(addMoreButtonText)}
          selectButtonText={t(selectButtonText)}
          openModal={openModal}
          onRemove={onRemove}
        />
      )}
      <AllMarketsModal
        title={t(modalTitle)}
        closeModal={() => closeModal()}
        isVisible={isModalVisible}
        numOfSelectedJurisdictions={selectedMarkets.length}
        description={priceBenefits || verificationPriceBenefits}
        summary={summary}
      >
        <MarketsCheckboxList
          markets="all"
          isSearchable
          isSearchFocusable={isModalVisible}
          alreadyCoveredMarkets={alreadyCoveredMarkets}
        />
        <MarketsContactBanner />
      </AllMarketsModal>
    </>
  )
}
